import { ParentProps, createSignal, createMemo, Show, For } from 'solid-js'
import { createStore } from 'solid-js/store'
import styles from './sis-mobile-configuration-viewer.module.css'

export class MobileConfigurationViewerProps {
    value?: string; // JSON string input
}

interface JsonTreeNodeProps {
    data: any;
    path: string;
    level: number;
    isLastItem?: boolean;
}

function isLookupObject(obj: any): boolean {
    return obj && typeof obj === 'object' &&
           'lookupDesc' in obj && 'lookupValue' in obj;
}

function formatLookupObject(obj: any): string {
    if (isLookupObject(obj)) {
        return `${obj.lookupDesc} (${obj.lookupValue})`;
    }
    return JSON.stringify(obj);
}

function JsonTreeNode(props: JsonTreeNodeProps) {
    const [expandedPaths, setExpandedPaths] = createStore<Record<string, boolean>>({});

    const isExpanded = createMemo(() => expandedPaths[props.path] ?? (props.level === 0));

    const toggleExpanded = () => {
        setExpandedPaths(props.path, !isExpanded());
    };

    const renderValue = () => {
        const { data } = props;

        if (data === null) {
            return <span class={styles.null}>null</span>;
        }

        if (typeof data === 'boolean') {
            return <span class={styles.boolean}>{data.toString()}</span>;
        }

        if (typeof data === 'number') {
            return <span class={styles.number}>{data}</span>;
        }

        if (typeof data === 'string') {
            return <span class={styles.string}>"{data}"</span>;
        }

        if (Array.isArray(data)) {
            return (
                <div>
                    <button
                        class={styles.expandButton}
                        onClick={toggleExpanded}
                        type="button"
                    >
                        {isExpanded() ? '▼' : '▶'} Array ({data.length} items)
                    </button>
                    <Show when={isExpanded()}>
                        <div class={styles.arrayContent}>
                            <For each={data}>
                                {(item, index) => (
                                    <div class={styles.arrayItem}>
                                        <span class={styles.arrayIndex}>[{index()}]:</span>
                                        <JsonTreeNode
                                            data={item}
                                            path={`${props.path}[${index()}]`}
                                            level={props.level + 1}
                                        />
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>
            );
        }

        if (typeof data === 'object') {
            const entries = Object.entries(data);

            // Special handling for lookup objects
            if (isLookupObject(data)) {
                return <span class={styles.lookup}>{formatLookupObject(data)}</span>;
            }

            return (
                <div>
                    <button
                        class={styles.expandButton}
                        onClick={toggleExpanded}
                        type="button"
                    >
                        {isExpanded() ? '▼' : '▶'} Object ({entries.length} properties)
                    </button>
                    <Show when={isExpanded()}>
                        <div class={styles.objectContent}>
                            <For each={entries}>
                                {([key, value], index) => (
                                    <div class={styles.objectProperty}>
                                        <span class={styles.propertyKey}>{key}:</span>
                                        <JsonTreeNode
                                            data={value}
                                            path={`${props.path}.${key}`}
                                            level={props.level + 1}
                                        />
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>
            );
        }

        return <span class={styles.unknown}>{String(data)}</span>;
    };

    return renderValue();
}

export default function MobileConfigurationViewer(props: ParentProps<MobileConfigurationViewerProps>) {
    const [parseError, setParseError] = createSignal<string | null>(null);

    const parsedData = createMemo(() => {
        if (!props.value) {
            return null;
        }

        try {
            setParseError(null);
            return JSON.parse(props.value);
        } catch (error) {
            setParseError(`Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
            return null;
        }
    });

    return (
        <div class={styles['sis-mobile-configuration-viewer']}>
            <Show when={parseError()}>
                <div class={styles.error}>
                    <h3>JSON Parse Error</h3>
                    <p>{parseError()}</p>
                </div>
            </Show>

            <Show when={parsedData() && !parseError()}>
                <JsonTreeNode
                    data={parsedData()}
                    path="root"
                    level={0}
                />
            </Show>

            <Show when={!props.value && !parseError()}>
                <div class={styles.empty}>
                    <p>No JSON data provided</p>
                </div>
            </Show>
        </div>
    );
}

MobileConfigurationViewer.defaultProps = new MobileConfigurationViewerProps();